/**
 * This is the main configuration class and application initialization.
 *
 * All configuration and object initialization takes place in this class.
 *
 * @author Lemuel Raganas
 */
import Vue from "vue";
import store from "@/store";
import App from "@/App.vue";
import ComponentRegistry from "@/libs/ComponentRegistry";
import WorldCupOption from "@/libs/world-cup/MainOption";
import WorldCupStub from "@/libs/world-cup/MainStub";
import DataRepoUtil from "@/libs/util/DataRepoUtil";
import DataRepoOption from "@/libs/world-cup/DataRepoOption";
import * as lob from "lodash/object";
import DataPoint from "@/libs/world-cup/DataPoint";
import ImagePoint from "@/libs/world-cup/ImagePoint";
import AssetPoint from "@/libs/world-cup/AssetPoint";
import MainApp from "@/libs/MainApp";
import ViewPortUtil from "@/libs/util/ViewPortUtil";
import { v4 as uuidGenerator } from "uuid";
import voca from "voca";
import { Host, HostResolver } from "@/libs/util/HostUtil";
import OnDataReadyCallback from "@/libs/common/delegate/OnDataReadyCallback";
import OnDebugCallback from "@/libs/common/delegate/OnDebugCallback";

export default class WorldCup {
  public static registry: any[] = [];
  private uid: string;
  private static viewportUtil: ViewPortUtil = new ViewPortUtil();
  private productionTip: boolean = false;
  private application: MainApp = <MainApp>{};
  private element: string = undefined;
  private component: string = undefined;
  private host: string = undefined;
  private dataPoint: DataPoint;
  private imagePoint: ImagePoint;
  private assetPoint: AssetPoint;
  private year: number = 0;
  private language: string = undefined;
  private includeOptionalResources: boolean = true;
  private defer: number = 1000;
  private data: any = undefined;
  private dataRepo: DataRepoOption[];
  private option: WorldCupOption;
  private dataRepoUtil: DataRepoUtil;
  private onReady: () => void;
  private translations: any;
  private onDataReadyCallback: OnDataReadyCallback;
  private onDebugCallback: OnDebugCallback;

  public static version = {
    major: 1,
    minor: 0,
    patch: 0,
    build: 202007031905,
    hash: "40617574686f72204c656d75656c20526167616e6173",
  };

  constructor(stub: WorldCupStub) {
    this.uid = "" + voca(uuidGenerator()).replaceAll("-", "");
    this.dataRepoUtil = new DataRepoUtil(stub.component, stub.dataRepo);
    this.productionTip = stub.productionTip;
    this.element = stub.element;
    this.component = stub.component;
    this.year = stub.year;
    this.language = stub.language || "en";
    this.onReady = stub.onReady;
    this.translations = stub.translations; // A quick translation fix. This is for the "Now! die die" situation

    // TODO: Will use this flag to include optional exteral libraries or not
    this.includeOptionalResources =
      stub.includeOptionalResources || this.includeOptionalResources;

    // TODO: Rename
    this.defer = stub.defer || this.defer;

    // Component specific configuration
    this.data = stub.data || {};

    // Configures how and when data is loaded
    this.dataRepo = this.dataRepoUtil.getDataRepo();

    // Generate default options
    this.option = this.getOptions(stub.option);

    if (!ComponentRegistry.hasComponent(this.component)) {
      this.component = "unknown";
    }

    // Auto detect appropriate data hosts
    // Users, will only need to specify the following:
    // production - Will resolve to production host
    // staging - Will resolve to staging host
    // localhost - Will default to http://localhost
    // empty/null/none - Will default to production
    const stubHost = lob.get(stub, "host", undefined);
    this.host = HostResolver.resolve(stubHost);

    // Below is initialization for all types of data points.
    // Configures default and check for any overriding values.
    this.dataPoint = this.getDataPoint(stub.dataPoint);
    this.imagePoint = this.getImagePoint(stub.imagePoint);
    this.assetPoint = this.getAssetPoint(stub.assetPoint);
  }

  private getOptions(option?: WorldCupOption) {
    const doption: WorldCupOption = {
      match:{
        show: "all",
        showGroupName: true,
        sortOrder: "asc",
        blinkInterval: "medium",
        backToTopButton: true,
        defaultMessage: {
          text: "",
          html: false,
        }
      },
      knockout:{
        show: "all",
        showGroupName: true,
        sortOrder: "asc",
        blinkInterval: "medium",
        defaultMessage: {
          text: "",
          html: false,
        },
        dateOfStage: {
          eighthFinals: "",
          quarterFinals: "",
          semiFinals: "",
          finals: ""
        }
      },
      mostRelevant: {
        show: "all",
        showGroupName: true,
        sortOrder: "asc",
        blinkInterval: "medium",
        matchIds: undefined,
        defaultMatchIds: [],
        defaultMessage: {
          text: "",
          html: false,
        }
      },
      navbar: {
        title: "WORLD CUP",
        items: [],
      },
      pointsTable: {
        backToTopButton: true,
      },
      notification: {
        show: true,
      },
      debug: {
        callback: undefined,
      },
      visible: true,
    };

    if (option === undefined) {
      return doption;
    }

    doption.match.show = lob.get(option, "match.show", "all");
    doption.match.showGroupName = lob.get(option, "match.showGroupName", true);
    doption.match.sortOrder = lob.get(option, "match.sortOrder", "asc");
    doption.match.defaultMessage.text = lob.get(option, "match.defaultMessage.text","");
    doption.match.defaultMessage.html = lob.get(option,"match.defaultMessage.html", false);
    doption.match.blinkInterval = lob.get(option, "match.blinkInterval", "medium");
    doption.match.backToTopButton = lob.get(option, "match.backToTopButton", true);
    doption.navbar.title = lob.get(option, "navbar.title", "WORLD CUP");
    doption.navbar.items = lob.get(option,"navbar.items", []);
    doption.pointsTable.backToTopButton = lob.get(option,"pointsTable.backToTopButton", true);
    doption.knockout.blinkInterval = lob.get(option, "knockout.blinkInterval", "medium");
    doption.knockout.dateOfStage.eighthFinals = lob.get(option, "knockout.dateOfStage.eighthFinals", "");
    doption.knockout.dateOfStage.quarterFinals = lob.get(option, "knockout.dateOfStage.quarterFinals", "");
    doption.knockout.dateOfStage.semiFinals = lob.get(option, "knockout.dateOfStage.semiFinals", "");
    doption.knockout.dateOfStage.finals = lob.get(option, "knockout.dateOfStage.finals", "");
    doption.mostRelevant.show = lob.get(option, "mostRelevant.show", "all");
    doption.mostRelevant.showGroupName = lob.get(option, "mostRelevant.showGroupName", true);
    doption.mostRelevant.sortOrder = lob.get(option, "mostRelevant.sortOrder", "asc");
    doption.mostRelevant.defaultMessage.text = lob.get(option, "mostRelevant.defaultMessage.text","");
    doption.mostRelevant.defaultMessage.html = lob.get(option,"mostRelevant.defaultMessage.html", false);
    doption.mostRelevant.blinkInterval = lob.get(option, "mostRelevant.blinkInterval", "medium");
    doption.mostRelevant.matchIds = lob.get(option, "mostRelevant.matchIds", undefined);
    doption.mostRelevant.defaultMatchIds = lob.get(option, "mostRelevant.defaultMatchIds", []);
    doption.notification.show = lob.get(option, "notification.show", true);
    doption.debug.callback = lob.get(option, "debug.callback", undefined);
    doption.visible = lob.get(option, "visible", true);
    return doption;
  }

  private getDataPoint(dataPoint: DataPoint): DataPoint {
    const defaultTimeout = 30000;
    if (dataPoint === undefined) {
      return {
        host: this.host,
        year: this.year,
        language: this.language,
        segment: undefined,
        api: undefined,
        timeout: defaultTimeout,
        connectionTimeout: 0,
        preload: true,
        postload: true,
        checksum: true,
      };
    }

    if (dataPoint.host === undefined) {
      dataPoint.host = this.host;
    }

    if (dataPoint.year === undefined) {
      dataPoint.year = this.year;
    }

    if (dataPoint.language === undefined) {
      dataPoint.language = this.language;
    }

    if (dataPoint.timeout === undefined) {
      dataPoint.timeout = defaultTimeout;
    }

    if (dataPoint.preload === undefined) {
      dataPoint.preload = true;
    }

    if (dataPoint.postload === undefined) {
      dataPoint.postload = true;
    }

    if (dataPoint.connectionTimeout === undefined) {
      dataPoint.connectionTimeout = 0;
    }

    if (dataPoint.checksum === undefined) {
      dataPoint.checksum = true;
    }

    return dataPoint;
  }

  private getImagePoint(imagePoint: ImagePoint): ImagePoint {
    if (imagePoint === undefined) {
      return {
        host: this.host,
        year: this.year,
        segment: undefined,
        url: undefined,
      };
    }

    imagePoint.host = lob.get(imagePoint, "host", this.host);
    imagePoint.year = lob.get(imagePoint, "year", this.year);
    imagePoint.segment = lob.get(imagePoint, "segment", undefined);
    imagePoint.url = lob.get(imagePoint, "url", undefined);

    return imagePoint;
  }

  private getAssetPoint(assetPoint: AssetPoint): AssetPoint {
    const ehost: Host = HostResolver.toHost(this.host);

    if (assetPoint === undefined) {
      return {
        host: this.host,
        year: this.year,
        segment:
          ehost !== Host.LOCALHOST ? "blueprint/cna/lib/ge-2020" : undefined,
      };
    }

    if (assetPoint.host === undefined) {
      assetPoint.host = this.host;
    }

    if (assetPoint.year === undefined) {
      assetPoint.year = this.year;
    }

    return assetPoint;
  }

  public static getVersion() {
    return (
      WorldCup.version.minor +
      "." +
      WorldCup.version.minor +
      "." +
      WorldCup.version.patch
    );
  }

  public static getFullVersion() {
    return (
      WorldCup.version.minor +
      "." +
      WorldCup.version.minor +
      "." +
      WorldCup.version.patch +
      "b" +
      WorldCup.version.build
    );
  }

  public static getBuildVersion() {
    return WorldCup.version.build;
  }

  public static getVersionInfo() {
    return WorldCup.version;
  }

  public static viewport(): ViewPortUtil {
    return WorldCup.viewportUtil;
  }

  public getUid() {
    return this.uid;
  }

  public stop() {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return;
    }

    const app: any = this.application.$children[0];
    app.stop();
  }

  public start() {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return;
    }

    const app: any = this.application.$children[0];
    app.start();
  }

  private static register(name: string, uid: string, instance: WorldCup) {
    this.registry.push({
      name: name,
      uid: uid,
      instance: instance,
    });
  }

  public setTimeout(timeout: number) {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return;
    }

    const app: any = this.application.$children[0];
    app.setTimeout(timeout);
  }

  public getAvailableComponents(): string[] {
    return ComponentRegistry.getComponents();
  }

  public getStore(name: string): any {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return undefined;
    }

    const app: any = this.application.$children[0];
    return app.getStore(name);
  }

  public getStoreNames(): string[] {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return undefined;
    }

    const app: any = this.application.$children[0];
    return app.getStoreNames();
  }

  public show() {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return;
    }

    const app: any = this.application.$children[0];
    return app.show();
  }

  public hide() {
    if (
      this.application === undefined &&
      this.application.$children.length <= 0
    ) {
      return;
    }

    const app: any = this.application.$children[0];
    return app.hide();
  }

  public onDataReady(callback?: OnDataReadyCallback) {
    this.onDataReadyCallback = callback;
  }

  private attachOnDataReady() {
    if (
      this.onDataReadyCallback === undefined ||
      this.onDataReadyCallback === null
    ) {
      return;
    }

    if (
      this.application === undefined ||
      this.application.$children === undefined ||
      this.application.$children.length <= 0
    ) {
      return;
    }

    const app: any = this.application.$children[0];
    return app.onDataReady(this.onDataReadyCallback);
  }

  public onDebug(callback?: OnDebugCallback) {
    this.onDebugCallback = callback;
  }

  private attachOnDebug() {
    if (
      this.onDebugCallback === undefined ||
      this.onDebugCallback === null ||
      this.application === undefined
    ) {
      return;
    }

    this.application.$config.option.debug.callback = this.onDebugCallback;
  }

  render() {
    Vue.config.productionTip = this.productionTip;
    const geOptions: WorldCupStub = {
      uid: this.uid,
      component: this.component,
      element: this.element,
      host: this.host,
      dataPoint: this.dataPoint,
      imagePoint: this.imagePoint,
      assetPoint: this.assetPoint,
      year: this.year,
      language: this.language,
      includeOptionalResources: this.includeOptionalResources,
      defer: this.defer,
      data: this.data,
      dataRepo: this.dataRepo,
      option: this.option,
      onReady: this.onReady,
      translations: this.translations,
    };
    this.application = new Vue({
      store,
      render: (h) => h(App),
    });
    this.application.$config = geOptions;

    // Register
    WorldCup.register(this.component, this.uid, this);

    // Actual mount
    this.application.$mount("#" + this.element);

    //////////////////////////////////////
    // Additional late bindings
    /////////////////////////////////////
    this.attachOnDataReady();
    this.attachOnDebug();
  }
}
